import { io, Socket } from 'socket.io-client';
import feathers from '@feathersjs/client';
import socketio from '@feathersjs/socketio-client';
import authentication from '@feathersjs/authentication-client';
import { WidgetConfig, MilesMessage, UserCoverImage } from '../types';
import {
  getAuthToken,
  setAuthToken,
  getAnonymousToken,
  setAnonymousToken,
  getConversationId,
  setConversationId,
  getUserData,
  setUserData,
  setDealerId,
  clearWidgetData,
  STORAGE_KEYS,
  getIsShowEnhanceView,
  getIsWebAppUser,
  setUserSessionId,
  getUserSessionId,
} from '../utils/storage';
import { buildQueryString } from '../utils/url';
import { IsNullOrEmpty } from '../utils/validation';

class WidgetAPI {
  private config: WidgetConfig;
  private socket: Socket | null = null;
  private feathersClient: any = null;
  private authToken: string | null = null;
  private anonymousToken: string | null = null;
  private conversationId: string | null = null;
  private sessionId: string | null = null;
  private isSocketAuthenticated: boolean = false;
  private socketPromise: Promise<Socket> | null = null;
  private showEnhanceView: boolean = true;
  private isWebAppUser: boolean = false;

  constructor(config: WidgetConfig) {
    this.config = config;

    // Load from encrypted storage
    this.authToken = getAuthToken();
    this.anonymousToken = getAnonymousToken();
    this.conversationId = getConversationId();
    this.showEnhanceView = getIsShowEnhanceView();
    this.isWebAppUser = getIsWebAppUser();
    this.sessionId = getUserSessionId();

    // Store dealer ID for future use
    setDealerId(config.dealerId);
  }

  async initSocket(): Promise<Socket> {
    const storedAuthToken = getAuthToken();
    const storedAnonymousToken = getAnonymousToken();
    const authTokenChanged = storedAuthToken !== this.authToken;

    this.authToken = storedAuthToken;
    this.anonymousToken = storedAnonymousToken;

    // Return existing socket if already connected (authenticated or not)
    if (this.socket && this.socket.connected) {
      if (this.authToken && (!this.isSocketAuthenticated || authTokenChanged)) {
        if (!this.feathersClient) {
          this.feathersClient = feathers();
          this.feathersClient.configure(socketio(this.socket, { timeout: 100000 }));
        }

        try {
          this.feathersClient.configure(
            authentication({
              storageKey: STORAGE_KEYS.AUTH_TOKEN,
              storage: window.localStorage,
            })
          );
          await this.feathersClient.authenticate({
            strategy: 'jwt',
            accessToken: this.authToken,
          });
          this.isSocketAuthenticated = true;
          console.log('[Miles Widget] Socket re-authenticated with JWT token');
        } catch (error) {
          console.error('[Miles Widget] Socket JWT re-authentication failed:', error);
          this.isSocketAuthenticated = false;
          clearWidgetData();
          this.authToken = null;
          this.conversationId = null;
        }
      }
      return this.socket;
    }

    // If a connection is already in progress, reuse the same promise
    if (this.socketPromise) {
      return this.socketPromise;
    }

    // Disconnect existing socket if any
    if (this.socket) {
      try {
        if (this.socket.connected) {
          this.socket.disconnect();
        }
      } catch (err) { }
      this.socket = null;
      this.feathersClient = null;
      this.isSocketAuthenticated = false;
    }

    // Create new socket
    this.socket = io(this.config.apiBase, {
      transports: ['websocket'],
      path: '/socket.io',
      reconnection: true,
      autoConnect: false,
    });

    // Create Feathers client with socket
    this.feathersClient = feathers();
    this.feathersClient.configure(socketio(this.socket, { timeout: 100000 }));
    this.feathersClient.configure(
      authentication({
        storageKey: this.authToken ? 'miles_widget_authkey' : STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN,
        storage: window.localStorage,
      })
    );

    // Connect the socket
    this.socket.connect();

    this.socketPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.socketPromise = null;
        reject(new Error('Socket connection timeout'));
      }, 10000);

      this.socket!.once('connect', async () => {
        clearTimeout(timeout);
        this.socketPromise = null;

        // Try to authenticate based on available token
        if (this.authToken) {
          // Authenticated user - use JWT authentication
          try {
            await this.feathersClient.reAuthenticate();
            this.isSocketAuthenticated = true;
            console.log('[Miles Widget] Socket authenticated with JWT token');
          } catch (error) {
            console.error('[Miles Widget] Socket JWT authentication failed:', error);
            this.isSocketAuthenticated = false;
            // Clear invalid token
            clearWidgetData();
            this.authToken = null;
            this.conversationId = null;
          }
        } else if (this.anonymousToken) {
          // Guest user - use anonymous authentication
          try {
            await this.feathersClient.authenticate({
              strategy: 'anonymous',
              accessToken: this.anonymousToken,
            });
            this.isSocketAuthenticated = false;
            console.log('[Miles Widget] Socket authenticated with anonymous token');
          } catch (error) {
            console.error('[Miles Widget] Socket anonymous authentication failed:', error);
            this.isSocketAuthenticated = false;
            // Clear invalid anonymous token
            this.anonymousToken = null;
            localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_AUTH_TOKEN);
          }
        } else {
          // No token available - socket connected but not authenticated
          this.isSocketAuthenticated = false;
          console.log('[Miles Widget] Socket connected without authentication');
        }

        // Resolve regardless of authentication status
        resolve(this.socket!);
      });

      this.socket!.on('connect_error', (error: Error) => {
        clearTimeout(timeout);
        this.socketPromise = null;
        console.error('[Miles Widget] Socket connection error:', error);
        reject(error);
      });

      this.socket!.on('disconnect', () => {
        this.isSocketAuthenticated = false;
      });
    });

    return this.socketPromise;
  }

  onMessage(callback: (message: MilesMessage) => void) {
    if (this.feathersClient) {
      // Use Feathers service event (same as qrbuddy-web)
      const milesMessageService = this.feathersClient.service('miles/message');
      milesMessageService.on('created', (message: MilesMessage) => {
        callback(message);
      });
    } else {
      console.error('[Miles API] Cannot set up message listener - feathersClient not initialized');
    }
  }

  offMessage(callback: (message: MilesMessage) => void) {
    if (this.feathersClient) {
      try {
        // Remove listener (same as qrbuddy-web)
        const milesMessageService = this.feathersClient.service('miles/message');
        milesMessageService.removeListener('created', callback);
      } catch (error) {
        console.error('[Miles API] Error removing message listener:', error);
      }
    } else {
    }
  }

  async sendIdentity(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone?: string;
  }): Promise<{ accessToken?: string; conversationId?: string; requestId?: string; user?: any }> {
    try {
      const response = await fetch(`${this.config.apiBase}/miles/widget/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealerId: this.config.dealerId,
          ...data,
          countryCode: '+1',
          pageUrl: window.location.href,
          guestConversationId: this.conversationId, // Pass existing guest conversation ID
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send your information. Please try again.';

        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  async verifyOTP(
    requestId: string,
    otp: string
  ): Promise<{ accessToken: string; conversationId: string; user?: any }> {
    try {
      const response = await fetch(`${this.config.apiBase}/miles/widget/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, otp }),
      });

      if (!response.ok) {
        let errorMessage = 'Invalid verification code. Please try again.';

        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  async anonymousLogin(): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiBase}/authentication`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strategy: 'anonymous' }),
      });

      if (!response.ok) {
        let errorMessage = 'Anonymous failed. Please try again.';

        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      this.setAnonymousAuthToken(result.accessToken);
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  async scanCar(
    url: string
  ): Promise<{ accessToken: string; conversationId: string; user?: any }> {
    if (this.authToken) {
      try {
        const response = await fetch(`${this.config.apiBase}/scan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.authToken || '' },
          body: JSON.stringify({ scanType: "car", url: url }),
        });

        if (!response.ok) {
          let errorMessage = 'Invalid verification code. Please try again.';

          try {
            const error = await response.json();
            errorMessage = error.message || error.error || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        return response.json();
      } catch (error: any) {
        if (error.message.includes('fetch')) {
          throw new Error('Unable to connect to server. Please check your internet connection.');
        }
        throw error;
      }
    } else {
      return {} as any;
    }
  }

  setAuth(token: string, conversationId?: string, user?: any) {
    this.authToken = token;
    setAuthToken(token); // Plain text storage (required by Feathers)

    if (conversationId) {
      this.conversationId = conversationId;
      setConversationId(conversationId); // Encrypted storage
    }

    if (user) {
      setUserData(user); // Encrypted storage
    }

    // Disconnect existing socket to force re-authentication with new token
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.feathersClient = null;
      this.isSocketAuthenticated = false;
    }
  }

  setAnonymousAuthToken(token: string) {
    this.anonymousToken = token;
    setAnonymousToken(token);

    // Disconnect existing socket to force re-authentication with new token
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.feathersClient = null;
      this.isSocketAuthenticated = false;
    }
  }

  setSessionId(sId: string) {
    this.sessionId = sId;
    setUserSessionId(sId);
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  getConversationId(): string | null {
    return this.conversationId;
  }

  getUserData(): any {
    return getUserData();
  }

  isAuthenticated(): boolean {
    return !!this.authToken;
  }

  isAnonymousAccess(): boolean {
    return !!this.anonymousToken;
  }
  
  isShowEnhanceView(): boolean {
    return !!this.showEnhanceView;
  }
  
  checkIsWebAppUser(): boolean {
    return !!this.isWebAppUser;
  }

  logout(): void {
    clearWidgetData();
    this.authToken = null;
    this.conversationId = null;
    this.isSocketAuthenticated = false;

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.feathersClient = null;
    }
  }

  private async socketCall(
    method: string,
    service: string,
    data: any
  ): Promise<any> {
    try {
      const socket = await this.initSocket();

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Request timed out. Please try again.'));
        }, 30000);

        socket.emit(method, service, data, (err: any, result: any) => {
          clearTimeout(timeout);
          if (err) {
            const errorMessage = err.message || err.error || 'An error occurred. Please try again.';
            reject(new Error(errorMessage));
          } else {
            resolve(result);
          }
        });
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to connect to chat service.');
    }
  }

  async loadMessages(conversationId: string, limit = 50): Promise<{ data: MilesMessage[] }> {
    try {
      // Use socket for both guest and authenticated users
      // Note: Backend hooks now allow unauthenticated access to 'find' for guests
      return await this.socketCall('find', 'miles/message', {
        conversationId,
        $limit: limit,
        $sort: { createdAt: 1 },
      });
    } catch (error: any) {
      // Fallback to HTTP if socket fails
      console.warn('[Miles Widget] Socket loadMessages failed, falling back to HTTP:', error);
      try {
        return await this.loadMessagesHTTP(conversationId, limit);
      } catch (httpError: any) {
        throw new Error(httpError.message || 'Failed to load message history.');
      }
    }
  }

  setGuestConversation(conversationId: string) {
    this.conversationId = conversationId;
    setConversationId(conversationId); // Save to encrypted storage
  }

  async createConversation(): Promise<{ id: string; _id: string }> {
    try {
      return await this.socketCall('create', 'miles/conversation', {
        dealerId: this.config.dealerId,
        context: {
          dealerId: this.config.dealerId,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          source: 'website_widget',
        },
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to start conversation. Please try again.');
    }
  }

  async sendMessage(
    conversationId: string,
    message: string,
    metadata?: any,
    messageType: import('../types').MessageType = 'text'
  ): Promise<void> {
    try {
      // Always use socket for both guest and authenticated users
      await this.socketCall('create', 'miles/message', {
        conversationId,
        senderType: 'user',
        message,
        messageType,
        metadata: {
          dealerId: this.config.dealerId,
          pageUrl: window.location.href,
          ...metadata,
        },
      });

      // Trigger AI response
      await this.socketCall('create', 'miles/ai-handler', {
        conversationId,
        userMessage: message,
        messageType,
        metadata: {
          dealerId: this.config.dealerId,
          ...metadata,
        },
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send message. Please try again.');
    }
  }

  async sendMessageHTTP(
    conversationId: string,
    message: string,
    metadata?: any,
    messageType: import('../types').MessageType = 'text'
  ): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiBase}/miles/widget/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': this.authToken || this.anonymousToken || '' },
        body: JSON.stringify({
          conversationId,
          message,
          messageType,
          dealerId: this.config.dealerId,
          metadata: {
            dealerId: this.config.dealerId,
            pageUrl: window.location.href,
            ...metadata,
          },
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send message. Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  async createGuestConversation(): Promise<{ conversationId: string }> {
    try {
      const response = await fetch(`${this.config.apiBase}/miles/widget/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.anonymousToken || ''
        },
        body: JSON.stringify({
          dealerId: this.config.dealerId,
          pageUrl: window.location.href,
          source: 'website_widget',
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to start conversation. Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  async loadMessagesHTTP(conversationId: string, limit = 50): Promise<{ data: MilesMessage[] }> {
    try {
      const response = await fetch(
        `${this.config.apiBase}/miles/widget/messages?conversationId=${conversationId}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': this.authToken || this.anonymousToken || ''
          },
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to load messages. Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  // When user clicks like button
  async handleLikeVehicle(carId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiBase}/miles/conversation/update-liked-vehicle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || ''
        },
        body: JSON.stringify({
          conversationId: this.conversationId,  // From existing conversation
          carId: carId,                 // Vehicle they liked
          isGuest: IsNullOrEmpty(this.authToken),
        })
      });

      return await response.json();

      // User can now ask Miles about this vehicle
    } catch (error) {
      console.error('Failed to update Miles context:', error);
    }
  }

  async getDealerInventoryOptions(queryParams: any): Promise<any> {
    try {
      queryParams.dealerId = this.config.dealerId;

      const queryString = buildQueryString(queryParams);
      const response = await fetch(`${this.config.apiBase}/dealer-inventory-options?${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Failed to get vehicles:', error);
    }
  }  

  async getVehicles(queryParams: any): Promise<any> {
    try {
      const queryString = buildQueryString(queryParams);
      const response = await fetch(`${this.config.apiBase}/cars?${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Failed to get vehicles:', error);
    }
  }  
  
  async getCar(carId: any): Promise<any> {
    try {
      const response = await fetch(`${this.config.apiBase}/cars/${carId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Failed to get vehicles:', error);
    }
  }

  async requestLeadAction({carId, actionType, scheduleDate, downPaymentAmount} : {
    carId: string;
    actionType: string;
    scheduleDate?: string | null;
    downPaymentAmount?: number | null;
  }): Promise<any> {
    const response = await fetch(`${this.config.apiBase}/request-lead-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || ''
        },
        body: JSON.stringify({
          carId,
          actionType,
          scheduleDate,
          downPaymentAmount,
          dealerId: this.config.dealerId,
        })
      });

    return await response.json();
  }

  async getSimilarVehicles(queryParams: any): Promise<any> {
    try {
      const queryString = buildQueryString(queryParams);
      const response = await fetch(`${this.config.apiBase}/similar-cars?${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Failed to get vehicles:', error);
    }
  }

  async createUserSession(): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiBase}/user-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
        body: JSON.stringify({
          conversationId: this.conversationId,
          dealerId: this.config.dealerId,
          entrySource: 'website',
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create user session. Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      this.setSessionId(result?.id ?? '');
    } catch (error: any) {
      if (error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }
  
  async createUserInteraction(data: {carId?: string, actionType: string}): Promise<void> {
    try {
      await fetch(`${this.config.apiBase}/user-interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.authToken || this.anonymousToken || '',
        },
        body: JSON.stringify({
          conversationId: this.conversationId,
          dealerId: this.config.dealerId,
          sessionId: this.sessionId,
          ...data,
        }),
      });      
    } catch (error: any) {
      // if (error.message.includes('fetch')) {
      //   throw new Error('Unable to connect to server. Please check your internet connection.');
      // }
      // throw error;
    }
  }

  async getDealer(): Promise<{ dealer_settings?: { dealershipHours?: string | null }, planStatus?: boolean | null, purchaseType?: string | null, userCoverImage?: UserCoverImage, hasActiveSalesMan?: boolean | null }> {
    try {
      const response = await fetch(
        `${this.config.apiBase}/dealers/${this.config.dealerId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        console.warn('[Miles Widget] Failed to fetch dealer information, using defaults');
        return {};
      }

      return response.json();
    } catch (error) {
      console.warn('[Miles Widget] Error fetching dealer information, using defaults:', error);
      return {};
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export default WidgetAPI;
